import api from './api';

/**
 * Service for managing users in the system
 */
const userService = {
  /**
   * Get all users
   * @returns {Promise} Promise object containing the users array
   */
  getAllUsers: async () => {
    const response = await api.get('/users');
    return response.data;
  },

  /**
   * Get a user by ID
   * @param {string|number} id User ID
   * @returns {Promise} Promise object containing the user data
   */
  getUserById: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  /**
   * Create a new user
   * @param {Object} userData User data object containing email, password, name, and role
   * @returns {Promise} Promise object containing the created user data
   */
  createUser: async (userData) => {
    const response = await api.post('/users', userData);
    return response.data;
  },

  /**
   * Update an existing user
   * @param {string|number} id User ID
   * @param {Object} userData User data object with fields to update
   * @returns {Promise} Promise object containing the updated user data
   */
  updateUser: async (id, userData) => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },

  /**
   * Delete a user
   * @param {string|number} id User ID to delete
   * @returns {Promise} Promise object with deletion status
   */
  deleteUser: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },

  /**
   * Get all available roles in the system
   * @returns {Promise} Promise object containing the roles array
   */
  getRoles: async () => {
    const response = await api.get('/users/roles');
    return response.data;
  },

  getAccountSummary: async () => {
    const response = await api.get('/users/accounts/summary');
    return response.data;
  },

  getTemporaryAccounts: async () => {
    const response = await api.get('/users/accounts');
    return response.data;
  },

  getCenters: async () => {
    const response = await api.get('/users/accounts/centers');
    return response.data;
  },

  createTemporaryAccount: async (accountData) => {
    const response = await api.post('/users/accounts', accountData);
    return response.data;
  },

  bulkCreateTemporaryAccounts: async (accounts) => {
    const response = await api.post('/users/accounts/bulk', { accounts });
    return response.data;
  },

  extendAccount: async (id, accountData) => {
    const response = await api.patch(`/users/accounts/${id}/extend`, accountData);
    return response.data;
  },

  extendExpiry: async (id, accountData) => {
    const response = await api.patch(`/users/accounts/${id}/extend`, accountData);
    return response.data;
  },

  updateExamQuota: async (id, payload) => {
    const response = await api.patch(`/users/accounts/${id}/extend`, {
      set_quota: payload.exam_quota !== undefined ? Number(payload.exam_quota) : Number(payload.set_quota),
    });
    return response.data;
  },

  addExamQuota: async (id, payload) => {
    const response = await api.patch(`/users/accounts/${id}/extend`, {
      add_quota: Number(payload.add_quota),
    });
    return response.data;
  },

  lockAccount: async (id) => {
    const response = await api.patch(`/users/accounts/${id}/lock`);
    return response.data;
  },

  unlockAccount: async (id) => {
    const response = await api.patch(`/users/accounts/${id}/unlock`);
    return response.data;
  },

  updateAccountStatus: async (id, action) => {
    const endpoint = action === 'unlock' ? `/users/accounts/${id}/unlock` : `/users/accounts/${id}/lock`;
    const response = await api.patch(endpoint);
    return response.data;
  },

  resetPassword: async (id, password) => {
    const response = await api.patch(`/users/accounts/${id}/reset-password`, { password });
    return response.data;
  },

  randomizeAvatar: async (id, style) => {
    const response = await api.patch(`/users/accounts/${id}/avatar`, style ? { style } : {});
    return response.data;
  },

  resetAccountPassword: async (id, password) => {
    const response = await api.patch(`/users/accounts/${id}/reset-password`, { password });
    return response.data;
  },

  deleteAccount: async (id) => {
    const response = await api.delete(`/users/accounts/${id}`);
    return response.data;
  },

  deleteManagedAccount: async (id) => {
    const response = await api.delete(`/users/accounts/${id}`);
    return response.data;
  },

  bulkDeleteAccounts: async (userIds) => {
    const response = await api.post('/users/accounts/bulk-delete', { user_ids: userIds });
    return response.data;
  },

  quickGenerateAccounts: async (generateData) => {
    const response = await api.post('/users/accounts/quick-generate', generateData);
    return response.data;
  },

  generateAccounts: async (generateData) => {
    const response = await api.post('/users/accounts/quick-generate', generateData);
    return response.data;
  },

  createAccount: async (accountData) => {
    const response = await api.post('/users/accounts', accountData);
    return response.data;
  },

  bulkCreateAccounts: async (accounts) => {
    const response = await api.post('/users/accounts/bulk', { accounts });
    return response.data;
  },

  completeProfile: async (profileData) => {
    const response = await api.post('/users/me/complete-profile', profileData);
    return response.data;
  },

  getAccountAuditLog: async (limit = 50) => {
    const response = await api.get(`/users/accounts/audit-log?limit=${limit}`);
    return response.data;
  }
};


export default userService;
